// Copyright (C) 2022 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import './Table.scss'
import { useEffect, useState } from 'react'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { useContextMenu } from './SectionContainer'


interface TableProps {
  className?: string
  contextPanel?: React.FC<{row: any} & any> | null
  contextPanelProps?: any
  columns: ColumnDefinition[]
  expand?: boolean
  fixed?: boolean
  height?: string
  items: any[]
  onDelete?: (obj: any, index: number) => void
  onRowClick?: (obj: any, index: number, onClose?: () => void) => void
}

interface ColumnDefinition {
  key: string
  label: string
  width: number
  render(row: any, index: number): string | React.ReactNode
  summarize?: (rows: any[]) => string
}



const TableColumnHeader: React.FC<{children: React.ReactNode, width: number}> = ({children, width}) => {
  const style = {maxWidth: width, minWidth: width, width: width}
  return (
    <th style={style}>
      <div className="column-header">
        {children}
      </div>
    </th>
  )
}


const TableRow: React.FC<{
  canDelete: boolean
  canSelect: boolean
  columns: ColumnDefinition[]
  index: number
  onDelete?: (obj: any, index: number) => void
  onRowClick?: (value: any, index: number) => void
  selected?: boolean
  value: any
}> = ({
  canDelete,
  canSelect,
  columns,
  index,
  onDelete,
  onRowClick,
  selected,
  value
}) => {
  const classNames: string[] = []
  const onClick = (onRowClick !== undefined) ? onRowClick : ((v: any, i: number) => {})
  if (onRowClick !== undefined) {
    classNames.push("selectable")
  }
  if (!canSelect) {
    classNames.push("selectable-disabled")
  }
  if (!!selected) {
    classNames.push("selected")
  }

  return (
    <tr
      className={classNames.join(' ')}
      onClick={() => {
        if (!selected) {
          onClick(value, index)
        }
      }}
    >
      {columns.map((column, columnIndex) => {
        return (
          <td key={columnIndex}>{column.render(value, index)}</td>
        )
      })}
      {(!canDelete || onDelete === undefined) ? null : (
        <td onClick={() => onDelete(value, index)} style={{width: "24px"}}>
          <FontAwesomeIcon icon={faTrash}/>
        </td>
      )}
    </tr>
  )
}


const Table: React.FC<TableProps> = ({
  className,
  contextPanel,
  contextPanelProps,
  columns,
  expand,
  fixed,
  height,
  items,
  onDelete,
  onRowClick
}) => {
  const classNames: string[] = ["virtual-viewport"]
  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const ctx = useContextMenu()
  const canDelete = !ctx.isOpen() && (onDelete !== undefined) && !contextPanel
  const canSelect = ctx.contextPanelComponent === null
    || (ctx.contextPanelComponent === contextPanel)
  const hasFooter = columns.filter((spec) => !!spec.summarize).length > 0
  if (!!className) {
    classNames.push(className)
  }
  if (!!expand) {
    classNames.push("expand")
  }
  const tableStyle: any = {
    tableLayout: (!!fixed) ? "fixed" : "auto"
  }
  const viewportStyle: any = {
    flex: "1 auto"
  }
  //const containerStyle = {overflow: "auto hidden", paddingBottom: "16px"}
  const containerStyle = {height: height}

  // Unselect the row if there is a different context panel open than the one
  // belonging to this table.
  useEffect(() => {
    if (!canSelect) {
      setSelectedRow(null)
    }
  }, [ctx.isOpen()])

  return (
    <div className={classNames.join(' ')} style={viewportStyle}>
      <div className="table-filter-container" style={{display: "none"}}>
      </div>
      <div style={{height: "100%"}}>
        <table style={{...tableStyle}}>
          <thead>
            <tr>
              {columns.map((column, index) => {
                return (
                  <TableColumnHeader key={index} width={column.width}>
                    {column.label}
                  </TableColumnHeader>
                )
              })}
              {(!canDelete) ? null : (
                <th style={{width: "24px"}}></th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((value: any, index: number) => {
              return (
                <TableRow
                  key={index}
                  canDelete={canDelete}
                  canSelect={canSelect}
                  columns={columns}
                  index={index}
                  selected={selectedRow === index}
                  onDelete={(v: any, index: number) => {
                    if (onDelete !== undefined) {
                      setSelectedRow(null)
                      onDelete(v, index)
                    }
                  }}
                  onRowClick={(v: any, index: number) => {
                    if (!canSelect) { return null }
                    if (!!contextPanel) {
                      let props = {
                        ...(contextPanelProps || {}),
                        row: v,
                        index: index
                      }
                      ctx.setContextMenu(contextPanel, props)
                    }
                    else if (onRowClick !== undefined) {
                      setSelectedRow(index)
                      onRowClick(v, index)
                    }
                  }}
                  value={value
                }/>
              )
            })}
          </tbody>
          {(!hasFooter) ? null : (<tfoot>
            <tr>
              {columns.map((column, index) => {
                return (!column.summarize)
                  ? (<th key={index}></th>)
                  : (<th key={index}>{column.summarize(items)}</th>)
              })}
              {(!canDelete) ? null : (
                <th style={{width: "24px"}}></th>
              )}
            </tr>
          </tfoot>)}
        </table>
      </div>
    </div>
  )
}


export {Table}